// dependencies
import React from "react";
import { createPortal } from "react-dom";

import { useKeyPress } from "../../../hooks/useKeyPress";
import { useOverlayAnimation } from "../../../hooks/useOverlayAnimation";
import { useOverlayFocus } from "../../../hooks/useOverlayFocus";
import { usePortal } from "../../../hooks/usePortal";

import { cn } from "../../../lib/utils";

export interface PanelBaseProps {
	/**
	 * React node content to display inside the panel
	 */
	children: React.ReactNode;
	/**
	 * Whether the panel is open or closed
	 */
	isOpen: boolean;
	/**
	 * Callback function executed when the panel should be closed
	 */
	onClose: () => void;
	/**
	 * Which side of the screen the panel should appear on
	 * @default "left"
	 */
	side?: "left" | "right";
	/**
	 * Optional CSS classes to apply to the panel container (use with caution)
	 */
	dangerouslySetClassName?: string;
}

/**
 * PanelBase component that provides a sliding panel with portal rendering.
 *
 * This component serves as an unstyled foundation for building panel templates.
 * It includes essential functionality like:
 * - Portal-based rendering outside the DOM hierarchy
 * - Left or right-side slide animations using Tailwind CSS
 * - Background scroll prevention when open
 * - Close on outside click behavior
 * - Close on Escape key press
 * - Fixed width of 420px (matching FilterPanel since it's our most common use case)
 *
 * Accessibility features:
 * - Uses appropriate ARIA attributes for modal behavior
 * - Traps focus within the panel when open
 * - Supports keyboard navigation (Escape to close)
 * - Prevents background interaction when open
 */
const PanelBase = (props: PanelBaseProps) => {
	const { children, isOpen, onClose, side = "left", dangerouslySetClassName } = props;

	// Animation lifecycle management
	const { shouldRender, isAnimating, animatedElementRef } = useOverlayAnimation<HTMLDivElement>({
		isOpen,
		transitionProperty: "transform",
	});

	// Portal and body scroll management with context support
	const { portalRef, isRendered, PortalProvider } = usePortal({
		shouldRender,
		portalAttribute: "data-panel-base-portal",
		preventBodyScroll: true,
	});

	// Focus management for accessibility
	useOverlayFocus<HTMLDivElement>({
		isOpen,
		shouldRender,
		isRendered,
		elementRef: animatedElementRef,
	});

	useKeyPress({
		key: "Escape",
		onPress: (e) => {
			if (!e.defaultPrevented) {
				onClose();
			}
		},
		disabled: !isOpen,
	});

	const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	if (!isRendered || !portalRef.current) {
		return null;
	}

	return createPortal(
		<PortalProvider>
			<div
				className={cn("fixed inset-0 z-50 flex", side === "right" ? "justify-end" : "")}
				role="dialog"
				aria-modal="true"
				aria-labelledby="panel-content"
			>
				<div
					className={cn(
						"fixed inset-0 bg-black/50 transition-opacity duration-300 ease-in-out",
						isAnimating ? "opacity-100" : "opacity-0"
					)}
					onClick={handleBackdropClick}
					aria-hidden="true"
				/>

				<div
					ref={animatedElementRef}
					className={cn(
						"relative z-10 flex h-full w-[420px] flex-col bg-white shadow-xl",
						"transform transition-transform duration-300 ease-in-out",
						isAnimating ? "translate-x-0" : side === "right" ? "translate-x-full" : "-translate-x-full",
						dangerouslySetClassName
					)}
					tabIndex={-1}
					id="panel-content"
				>
					{children}
				</div>
			</div>
		</PortalProvider>,
		portalRef.current
	);
};

PanelBase.displayName = "PanelBase";

export { PanelBase };
